// ---------------------------------------------------------------------------
// Homefront v3 — surveillance drones
// ---------------------------------------------------------------------------
//
// A drone is a single-use, unarmed intel asset bought with yield (DRONE_COST)
// and launched from one of your live structure cells the same round. It must
// target a subplot on the opponent's side of the SAME corridor its launch
// structure sits under; at resolution it reveals 4 subplots — the target
// and three of its neighbours, every other one round the ring, so the target
// stays the centre of a three-armed propeller (clipped at field edges,
// spilling freely across corridor seams — the field is continuous) — and
// stamps nearest-structure distance intel at the target cell, exactly like
// a crater's bearing.
//
// Scanned cells are revealed PERMANENTLY and carry `scanned: true` so they
// render as intel, distinct from crater damage — on both boards: the attacker
// sees what's there; the defender keeps a permanent marker of what leaked (and
// can infer a structure stands in that corridor on the attacker's side, since
// drones only fly their own corridor). The exact launch cell is never shown to
// the defender. Drones cannot be intercepted by rockets.

import { cloneHomefront } from './subplotGrid.js'
import { HEX_DIRS, cellsAtOffsets } from './hexGrid.js'
import { stampDistanceIntel } from './subplotDamage.js'

// The 4 subplots a drone reveals: the target cell + alternate neighbours
// (HEX_DIRS 0, 2, 4 — a rotationally symmetric propeller whose straight arm
// points toward the band on either Grid), clipped at field edges.
export const SCAN_ARMS = [HEX_DIRS[0], HEX_DIRS[2], HEX_DIRS[4]]
export function droneScanCells(targetS) {
    return [targetS, ...cellsAtOffsets(targetS, SCAN_ARMS)]
}

// Applies one drone scan to the DEFENDER's homefront. Pure: returns a fresh
// homefront plus the scanned cells. Distance intel reads the homefront as
// handed in — call after craters resolve so it reports post-clash survivors.
export function applyDroneScan(homefront, targetS) {
    const hf = cloneHomefront(homefront)
    const cells = droneScanCells(targetS)
    for (const s of cells) {
        const cell = hf.subplots[s]
        if (!cell) continue
        cell.revealed = true
        cell.scanned = true
    }
    // Tagged like a crater's bearing (same helper): letter + distance, so a
    // scan's stamp joins the same per-war letter ledger as the strikes'.
    stampDistanceIntel(hf, targetS)
    return { homefront: hf, cells }
}

// Ages the previous round's snapshot frames. A scanned cell keeps its
// lightened wash permanently, but the purple frame marks only the round the
// snapshot was taken: at each resolution — BEFORE the round's new scans
// land — every `scanned: true` becomes `scanned: 'aged'` (still truthy, so
// every other scanned behavior persists). A re-scan overwrites back to true,
// re-freshening the frame. Pure, deterministic on both clients.
export function ageDroneScans(homefront) {
    const hf = cloneHomefront(homefront)
    for (const cell of hf.subplots) {
        if (cell?.scanned === true) cell.scanned = 'aged'
    }
    return hf
}
